/**
 * patientFileTypes.ts
 *
 * The file types a patient's Files take, so the staff app can turn a wrong one
 * away before sending any of it. The server's list in
 * api/app/Support/PatientFileTypes.php decides; this is its copy, and
 * api/tests/Unit/PatientFileTypesTest.php compares the two.
 */

export const ALLOWED_EXTENSIONS: readonly string[] = [
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg',
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'csv', 'txt', 'rtf', 'mp4', 'mov', 'avi', 'webm',
  'mp3', 'wav', 'zip', 'rar', 'heic', 'tiff', 'tif',
];

const READABLE_TYPES =
  'a photo (JPG, PNG, GIF, WEBP, BMP, SVG, HEIC, TIFF), a document (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, CSV, TXT, RTF), a video (MP4, MOV, AVI, WEBM), an audio file (MP3, WAV), or a ZIP or RAR file';

/** For a file input's `accept`: every allowed type as a file ending. */
export const ACCEPT = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',');

/** The characters PHP's trim() removes, which is what the server trims. */
const PHP_TRIM = /^[ \t\n\r\0\x0B]+|[ \t\n\r\0\x0B]+$/g;

/**
 * Lower-cased type at the end of a file name, or '' when it has none. Spaces
 * around the name are not part of it: the server saves the trimmed name.
 */
export function extensionOf(name: string): string {
  const trimmed = name.replace(PHP_TRIM, '');
  const base = trimmed.slice(trimmed.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot === -1 ? '' : base.slice(dot + 1).toLowerCase();
}

export function isAllowed(name: string): boolean {
  const ext = extensionOf(name);
  return ext !== '' && ALLOWED_EXTENSIONS.includes(ext);
}

/** What staff see when a file is refused for its type. */
export function uploadRejectionMessage(rawExt: string): string {
  const ext = rawExt.replace(PHP_TRIM, '').toLowerCase();
  const lead = ext === ''
    ? "This file has no file type, so it can't be uploaded."
    : `This file type (.${ext}) can't be uploaded.`;
  return `${lead} Upload ${READABLE_TYPES} instead.`;
}
